import { ChangeEvent, FC, MouseEvent } from 'react';
import { css, Theme } from '@emotion/react';
import { MenuItem } from '@mui/material';
import MuiButton from '@mui/material/Button';
import MuiButtonGroup from '@mui/material/ButtonGroup';
import { ButtonGroupProps as MuiButtonGroupProps } from '@mui/material/ButtonGroup/ButtonGroup';
import useTranslation from 'next-translate/useTranslation';

import { TextField } from '@/shared/TextField';
import { AccessLevel } from '@/types/invite-member.type';


type AccessLevelPickerProps = {
  onAccessLvlChange: (role: AccessLevel) => void;
  accessLvlValue: AccessLevel;
  variant?: 'select' | 'buttons';
  allowedAccessLevels?: AccessLevel[];
}

type AccessLvlBtn = {
  accessLvl: AccessLevel;
  btnText: string;
}

const accessLvlButtons: AccessLvlBtn[] = [
  { accessLvl: 'admin', btnText: 'Admin' },
  { accessLvl: 'manager', btnText: 'Manager' },
  { accessLvl: 'user', btnText: 'User' },
];

const accessLvlButtonsSelector: AccessLvlBtn[] = [...accessLvlButtons];
accessLvlButtonsSelector.splice(2, 1, { accessLvl: 'user', btnText: 'Regular User' });

const filterAccessLevels = (buttons: AccessLvlBtn[], allowed?: AccessLevel[]) =>
  allowed ? buttons.filter(({ accessLvl }) => allowed.includes(accessLvl)) : buttons;

const styles = {
  TextField: (theme: Theme) => css`
    min-width: 192px;

    ${ theme.breakpoints.down('sm') } {
      width: 100%;
    }
  `,
};

export const AccessLevelPicker: FC<AccessLevelPickerProps> = ({
  onAccessLvlChange,
  accessLvlValue,
  variant,
  allowedAccessLevels,
}) => {
  const handleAccessLvlChange = (e: ChangeEvent<HTMLInputElement> | MouseEvent<HTMLDivElement>) => {
    const newAccessLvl = (e.target as HTMLInputElement).value as AccessLevel;
    onAccessLvlChange(newAccessLvl);
  };

  return variant === 'select'
    ? (
      <RoleSelector
        currentAccessLvl={ accessLvlValue }
        onChangeAccessLvl={ handleAccessLvlChange }
        allowedAccessLevels={ allowedAccessLevels }
      />
    )
    : (
      <RoleButtons
        currentAccessLvl={ accessLvlValue }
        onClick={ handleAccessLvlChange }
        allowedAccessLevels={ allowedAccessLevels }
        fullWidth
      />
    );
};


const RoleButtons: FC<MuiButtonGroupProps & {
  currentAccessLvl: AccessLevel;
  allowedAccessLevels?: AccessLevel[];
}> = ({ currentAccessLvl, allowedAccessLevels, ...btnGroupProps }) => (
  <MuiButtonGroup { ...btnGroupProps } fullWidth>
    {
      filterAccessLevels(accessLvlButtons, allowedAccessLevels).map(({ accessLvl, btnText }) => (
        <MuiButton
          key={ accessLvl }
          value={ accessLvl }
          aria-selected={ accessLvl === currentAccessLvl }
          fullWidth
        >
          { btnText }
        </MuiButton>
      ))
    }
  </MuiButtonGroup>
);

const RoleSelector: FC<{
  currentAccessLvl: AccessLevel,
  onChangeAccessLvl: (e: ChangeEvent<HTMLInputElement>) => void;
  allowedAccessLevels?: AccessLevel[];
}> = ({ currentAccessLvl, onChangeAccessLvl, allowedAccessLevels }) => {
  const { t } = useTranslation();
  return (
    <TextField
      select
      label={ t('onboarding:invite_members_step.access_level') }
      value={ currentAccessLvl }
      onChange={ onChangeAccessLvl }
      variant="filled"
      css={ styles.TextField }
      InputLabelProps={{
        sx: {
          color: '#9A918D !important',
        },
      }}
      InputProps={{
        sx: {
          '& .MuiSelect-icon': {
            color: '#574841',
          },
        },
      }}
    >
      { filterAccessLevels(accessLvlButtonsSelector, allowedAccessLevels).map(({ accessLvl, btnText }) => (
        <MenuItem key={ accessLvl } value={ accessLvl }>
          { btnText }
        </MenuItem>
      )) }
    </TextField>
  );
};
